import type { Grade } from "@core/grades/types";
import { URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowUpLeft, GraduationCap } from "lucide-react";

const gradeTones = [
  "from-violet-100 to-fuchsia-50 text-violet-700 dark:from-violet-950 dark:to-fuchsia-950/30 dark:text-violet-300",
  "from-cyan-100 to-sky-50 text-cyan-700 dark:from-cyan-950 dark:to-sky-950/30 dark:text-cyan-300",
  "from-amber-100 to-orange-50 text-amber-700 dark:from-amber-950 dark:to-orange-950/30 dark:text-amber-300",
  "from-emerald-100 to-lime-50 text-emerald-700 dark:from-emerald-950 dark:to-lime-950/30 dark:text-emerald-300",
] as const;

export function ModernHomeGuestGrades({ grades = [] }: { grades?: Grade[] }) {
  if (grades.length === 0) return null;

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-7">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-600 text-white shadow-lg shadow-violet-600/20">
            <GraduationCap className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-xl font-black text-slate-950 dark:text-white">
              {trans("home.allGrades")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {trans("home.allGradesDescription")}
            </p>
          </div>
        </div>
        <Link
          to={URLS.grades.list}
          className="text-sm font-black text-violet-700 dark:text-violet-300"
        >
          {trans("home.viewAll")}
        </Link>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {grades.map((grade, index) => (
          <Link
            key={grade.id}
            to={URLS.grades.view(grade)}
            className={`group relative min-h-32 overflow-hidden rounded-3xl bg-gradient-to-br p-4 transition duration-300 hover:-translate-y-1 hover:shadow-lg ${gradeTones[index % gradeTones.length]}`}
          >
            <span className="absolute -end-2 -top-7 text-[7rem] font-black opacity-10">
              {grade.number || index + 1}
            </span>
            <div className="relative flex h-full items-end justify-between gap-3">
              <div>
                <span className="text-xs font-black opacity-70">
                  {trans("home.gradeLevel")}
                </span>
                <h3 className="mt-1 text-lg font-black text-slate-950 dark:text-white">
                  {grade.name}
                </h3>
              </div>
              <ArrowUpLeft className="h-5 w-5 shrink-0 transition group-hover:-translate-y-0.5 ltr:rotate-90 rtl:rotate-0" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
